import { Maybe } from 'ballerina-core'
import { MediaController } from 'media-chrome'
import React, { MutableRefObject, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useEventListener } from '../hooks/useEventListener'
import { cn } from '../utils/addToClassName'
import { htmlDecode } from '../utils/html'
import { reactContainer } from '../utils/reactContainer'
import { SVGIcon } from '../utils/svg'
import { Viewport } from '../utils/viewport'
import { Img, ImgProps } from './img'
import { Video } from './video'

//FIXME: Lightbox currently needs to be remounted every time index changes

export const Lightbox = ({
  id,
  index,
  totalCount,
  videoCookieAccepted,
  acceptVideoCookie,
  caption,
  source,
  className,
  viewport,
  lightboxTrigger,
  onClose,
  onNext,
  onPrev,
  ...media
}: {
  id: string
  index: number
  totalCount: number
  videoCookieAccepted: boolean
  acceptVideoCookie: () => void
  caption?: string
  source?: string
  className?: string
  viewport: Viewport
  lightboxTrigger: HTMLButtonElement
} & ({ kind: 'image'; image: Pick<ImgProps, 'alt' | 'src' | 'crops'> } | { kind: 'video'; url: string }) &
  LightboxEvents) => {
  const captionElementRef = useRef<HTMLDivElement>(null)
  const navElementRef = useRef<HTMLElement>(null)
  const visualElementRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const playerRef = useRef<MediaController>(null)
  const prevNavButtonRef = useRef<HTMLButtonElement>(null)
  const nextNavButtonRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const [imageLoaded, setImageLoaded] = useState(false)
  const [captionTooLong, setCaptionTooLong] = useState<boolean | 'unknown'>('unknown')
  const [captionTruncated, setCaptionTruncated] = useState(true)

  useEffect(() => {
    // Set `inert` attribute on entire react container while modal is open
    if (reactContainer != null) reactContainer.setAttribute('inert', '')

    addLightboxToggledClass()

    return () => {
      // Remove `inert` attribute from entire react container
      if (reactContainer != null) reactContainer.removeAttribute('inert')

      // Restore focus on last focused
      lightboxTrigger.focus()
    }
  }, [])

  useEventListener('keyup', (e) => {
    if (e.key === 'Escape') {
      highlightKeyPressedButton(closeButtonRef)
      onClose()
      removeLightboxToggledClass()
      e.stopPropagation()
    }
    if (e.key === 'ArrowLeft') {
      highlightKeyPressedButton(prevNavButtonRef)
      onPrev()
      e.stopPropagation()
    }
    if (e.key === 'ArrowRight') {
      highlightKeyPressedButton(nextNavButtonRef)
      onNext()
      e.stopPropagation()
    }
  })

  // Caption truncation
  useEffect(() => {
    const captionTooLong = captionElementRef.current
      ? captionElementRef.current.scrollHeight > captionElementRef.current.clientHeight
      : false
    setCaptionTooLong(captionTooLong)
    setCaptionTruncated(captionTooLong)
  }, [viewport.width, viewport.height, media.kind === 'image' ? media.image?.src : media.url])

  // Aspect-ratio
  useLayoutEffect(() => {
    const navElement = navElementRef.current
    const visualElement = visualElementRef.current

    if (navElement && visualElement) {
      // Image
      const imgElement = imgRef.current
      if (imgElement && imageLoaded) {
        const { naturalHeight, naturalWidth } = imgElement
        const aspectRatio = naturalWidth / naturalHeight
        const maxImageHeight = viewport.height - navElement.offsetHeight
        const maxImageWidth = maxImageHeight * aspectRatio
        const maxSize = Math.min(viewport.width, maxImageWidth)

        imgElement.style.width = `${maxSize}px`
        imgElement.style.height = `${maxImageHeight}px`
        imgElement.style.maxWidth = 'unset'
        imgElement.style.maxHeight = 'unset'
        imgElement.style.objectFit = 'contain'
      }

      // Video
      const playerElement = playerRef.current
      if (playerElement) {
        const aspectRatio = 16 / 9
        const maxImageHeight = viewport.height - navElement.offsetHeight
        const maxImageWidth = maxImageHeight * aspectRatio
        const maxSize = Math.min(viewport.width, maxImageWidth)

        visualElement.style.width = `${maxSize}px`
        visualElement.style.height = `${maxImageHeight}px`
        visualElement.style.display = 'grid'
        visualElement.style.alignItems = 'center'
        visualElement.style.marginInline = 'auto'
      }
    }
  }, [
    viewport.width,
    viewport.height,
    imageLoaded,
    captionTruncated, // Needed to recalculate image dimensions when toggling caption
  ])

  const currentImageCount = index + 1

  return createPortal(
    <div
      id={`a11y-lightbox-${id}`}
      role="dialog"
      aria-modal="true"
      className={cn(`lightbox lightbox--${media.kind}`, className)}
    >
      <figure className="lightbox__figure">
        <div className="lightbox__figure-inner">
          <div ref={visualElementRef} className="lightbox__visual">
            {media.kind === 'image'
              ? media.image && (
                  <Img
                    {...media.image}
                    imgRef={imgRef}
                    className="lightbox__image"
                    loading="lazy"
                    loadedAndSetLoaded={[imageLoaded, setImageLoaded]}
                  />
                )
              : media.url && (
                  <Video
                    url={media.url}
                    videoCookieAccepted={videoCookieAccepted}
                    acceptVideoCookie={acceptVideoCookie}
                  />
                )}
          </div>
        </div>

        <figcaption
          ref={navElementRef}
          style={media.kind == 'image' && !imageLoaded ? { opacity: 0 } : undefined}
          className={`lightbox__figcaption lightbox-figcaption ${
            captionTooLong == true ? 'lightbox-figcaption--truncatable' : ''
          }${captionTruncated ? ' lightbox-figcaption--truncated' : ' lightbox-figcaption--full'}`}
        >
          <div className="lightbox-figcaption__inner ls-container ls-gutter">
            {/* ----- Caption & Source */}
            <div className={`lightbox-figcaption__text-wrapper ls-container--s`}>
              {(caption || source) && (
                <p
                  id={`a11y-lightbox-caption-${id}`}
                  ref={captionElementRef}
                  aria-expanded={captionTooLong == true ? !captionTruncated : undefined}
                  className="lightbox-figcaption__text"
                  onClick={() => (captionTooLong == true ? setCaptionTruncated(!captionTruncated) : undefined)}
                  style={
                    captionTooLong == 'unknown' || (captionTooLong == true && captionTruncated)
                      ? {
                          WebkitLineClamp: captionMaxLines,
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }
                      : undefined
                  }
                  dangerouslySetInnerHTML={{
                    __html:
                      source && caption
                        ? `${htmlDecode(caption)} (${htmlDecode(source)})`
                        : htmlDecode(source) || htmlDecode(caption) || '',
                  }}
                />
              )}

              {/* Read more toggle */}
              {captionTooLong == true && (
                <button
                  className="lightbox-figcaption__toggler"
                  aria-controls={`a11y-lightbox-caption-${id}`}
                  onClick={() => setCaptionTruncated(!captionTruncated)}
                >
                  {captionTruncated ? 'Read more' : 'Read less'}
                </button>
              )}
            </div>

            {/* ----- Controls */}
            <nav className="lightbox__nav lightbox-nav" aria-label="Pagination">
              {/* Counter */}
              <div className="lightbox-nav__item">
                <div className="lightbox__counter">
                  <abbr
                    title={`Image ${currentImageCount} of ${totalCount}`}
                  >{`${currentImageCount}/${totalCount}`}</abbr>
                </div>
              </div>

              {/* Prev/Next */}
              {totalCount > 1 && (
                <div className="lightbox-nav__item">
                  <div className="lightbox-nav__controls">
                    <NavigationButton
                      kind="prev"
                      btnRef={prevNavButtonRef}
                      onClick={onPrev}
                      index={index}
                      totalCount={totalCount}
                    />
                    <NavigationButton
                      kind="next"
                      btnRef={nextNavButtonRef}
                      onClick={onNext}
                      index={index}
                      totalCount={totalCount}
                    />
                  </div>
                </div>
              )}

              {/* Close */}
              <div className="lightbox-nav__item">
                <button
                  ref={closeButtonRef}
                  className="lightbox-nav__clickable lightbox-nav__clickable--close"
                  onClick={(e): void => {
                    onClose()
                    removeLightboxToggledClass()
                    e.stopPropagation()
                  }}
                >
                  <span className="sr-text">Close</span>
                  <SVGIcon id="icon-cross" size={30} />
                </button>
              </div>
            </nav>
          </div>
        </figcaption>
      </figure>
    </div>,
    document.body
  )
}

export type LightboxEvents = {
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}

// On keypress, highlight the button briefly with the hover styling (activeClickableClass)
//TODO: this does not work until Lightbox works without remounting
const highlightKeyPressedButton = (buttonRef: RefObject<HTMLButtonElement>) => {
  const button = buttonRef.current
  if (button !== null && !button.disabled) {
    if (button.classList.contains(activeClickableClass)) button.classList.remove(activeClickableClass)
    else {
      button.classList.add(activeClickableClass)
      button.addEventListener('transitionend', () => button.classList.remove(activeClickableClass), {
        once: true,
      })
    }
  }
}

export const LightboxTrigger = ({
  id,
  children,
  className,
  isForeground,
  onOpen,
  lightboxTriggerRef,
}: {
  id: string
  className: string
  isForeground: boolean
  children: ReactNode
  onOpen: () => void
  lightboxTriggerRef: MutableRefObject<Maybe<HTMLButtonElement>>
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  return (
    <button
      ref={buttonRef}
      aria-expanded={isForeground}
      aria-controls={`a11y-lightbox-${id}`}
      onClick={(e): void => {
        onOpen()
        e.stopPropagation()
        if (buttonRef.current) lightboxTriggerRef.current = buttonRef.current
      }}
      className={className}
    >
      {children}
    </button>
  )
}

const activeClickableClass = 'lightbox-clickable--active'

const addLightboxToggledClass = () => document.documentElement.classList.add(toggledClass, overflowClass)
const removeLightboxToggledClass = () => document.documentElement.classList.remove(toggledClass, overflowClass)
const toggledClass = 'lightbox--toggled'
const overflowClass = 'overflow-hidden'

const NavigationButton = ({
  kind,
  index,
  totalCount,
  btnRef,
  onClick,
}: {
  kind: 'prev' | 'next'
  index: number
  totalCount: number
  btnRef: RefObject<HTMLButtonElement>
  onClick: () => void
}) => {
  const isFirst = index === 0
  const isLast = index + 1 >= totalCount
  const isDisabled = kind === 'prev' ? isFirst : isLast

  const prevOrNext = kind === 'prev' ? 'Previous' : 'Next'

  return (
    <button
      ref={btnRef}
      className={`lightbox-nav__clickable lightbox-nav__clickable--${kind}`}
      title={prevOrNext}
      disabled={isDisabled}
      onClick={
        isDisabled
          ? undefined
          : (e): void => {
              onClick()
              e.stopPropagation()
            }
      }
    >
      <span className="sr-text">{prevOrNext}</span>
      <SVGIcon id={kind === 'prev' ? 'icon-arrow-left' : 'icon-arrow-right'} size={30} />
    </button>
  )
}

const captionMaxLines = 2
